import type { PeerMetadata, TuningTrialContext } from '@playcastdotio/Messaging';
import type { TuningWireMessage } from '@playcastdotio/PeerCore';

// Re-export shared peer connection types from PeerCore
export type {
  IPeerConnection,
  IPeerConnectionFactory,
  PeerConnectionOptions,
  PeerConnectionEvent,
  TransceiverSpec,
  TransceiverKind,
} from '@playcastdotio/PeerCore';

// ========================================
// Host-specific Peer Types
// ========================================

export type PeerType = 'player' | 'remoteHost' | 'admin';

export type PeerPermissions = {
  mouse: boolean;
  keyboard: boolean;
  touch: boolean;
  gamepad: boolean;
};

export const DEFAULT_PERMISSIONS: Record<PeerType, PeerPermissions> = {
  player: { mouse: true, keyboard: true, touch: true, gamepad: true },
  admin: { mouse: true, keyboard: true, touch: true, gamepad: true },
  remoteHost: { mouse: false, keyboard: false, touch: false, gamepad: false },
};

export type ConnectedPeer = {
  peerId: string;
  peerType: PeerType;
  metadata: PeerMetadata;
  permissions: PeerPermissions;
  connectedAt: number;
};

// ========================================
// Serializable Message Types
// ========================================

export type SerializableMessage = {
  type: string;
  payload: Record<string, unknown>;
};

// ========================================
// Signaling Service Interface (serializable boundary)
// ========================================

export type SignalingMessage = {
  tuningTrial?: TuningTrialContext;
  control?: TuningWireMessage;
  type: 'offer' | 'answer' | 'ice-candidate' | 'ice-complete' | 'tuning-control';
  fromId: string;
  toId: string;
  sdp?: RTCSessionDescriptionInit;
  candidates?: RTCIceCandidateInit[];
  peerMetadata?: PeerMetadata;
  peerIdType?: string;
};

export type PeerIdAssignedData = {
  peerId: string;
  peerIdType: string;
  iceServers: RTCIceServer[];
};

export type ISignalingService = {
  sendOffer(peerId: string, targetPeerId: string, sdp: RTCSessionDescriptionInit): void;
  sendAnswer(peerId: string, targetPeerId: string, sdp: RTCSessionDescriptionInit, tuningTrial?: TuningTrialContext): void;
  sendIceCandidate(peerId: string, targetPeerId: string, candidates: RTCIceCandidateInit[], tuningTrial?: TuningTrialContext): void;
  sendTuningControl?(peerId: string, targetPeerId: string, message: TuningWireMessage): boolean;
  requestPeerId(type: string, metadata: PeerMetadata): void;
  onPeerIdAssigned(handler: (data: PeerIdAssignedData) => void): () => void;
  onSignalingMessage(handler: (msg: SignalingMessage) => void): () => void;
};

// ========================================
// Peer Type Handler Registry
// ========================================

export type PeerTypeHandler = {
  onConnected(peerId: string, metadata: PeerMetadata): void;
  onDisconnected(peerId: string): void;
  onMessage(peerId: string, message: SerializableMessage): void;
};
